import { templateIdForServer } from '@electron/shared/mcp-template-gating';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type RefCallback } from 'react';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { SHOWN_AFTER_MS } from '@/features/action-cards-view/feed/useFeedImpression';
import { startChatWithPrompt } from '@/features/chat/start-chat-with-prompt';
import { FEATURES, hasFeature } from '@/features/feature-flags';
import {
  INTEGRATION_SUGGESTIONS_REFRESH_INTERVAL_MS,
  integrationKeys,
  useDismissIntegrationSuggestionMutation,
  useIntegrationSuggestionsQuery,
} from '@/features/integrations/integrations.query';
import {
  supportedOnThisDevice,
  type IntegrationSuggestion,
  type IntegrationTarget,
} from '@/features/integrations/integrations.types';
import { mcpKeys } from '@/features/mcp/mcp.query';
import { useMcpServersQuery } from '@/features/settings/sub-settings/settings-local-mcp.query';
import { useInViewport } from '@/hooks/useInViewport';
import { useTabVisible } from '@/hooks/useTabVisible';
import { useDevicePermissionGrants } from '@/integrations/device-integrations';
import { connectIntegration } from '@/integrations/integrations.utils';
import { openOnDeviceSetup } from '@/integrations/on-device-setup';
import { listenWithAbortIPC } from '@/utils/abort';
import { trackEvent } from '@/utils/analytics';
import { inElectron } from '@/utils/capabilities';

import { suggestionExperimentProperties } from './suggestion-experiment';
import { visibleSuggestions } from './visible-suggestions';

// Home, the Feed and Integrations remount often, so each suggestion counts as shown once per surface per app session
const shownSuggestions = new Set<string>();

export type SuggestionSurface = 'home' | 'feed' | 'integrations';

export const trackSuggestionShown = (
  suggestion: IntegrationSuggestion,
  position: number,
  surface: SuggestionSurface
): void => {
  const experiment = suggestionExperimentProperties(suggestion, surface);
  const key = `${surface}:${suggestion.action}:${suggestion.account_id ?? suggestion.toolkit_slug}:${suggestion.source}:${JSON.stringify(experiment)}`;
  if (shownSuggestions.has(key)) return;
  shownSuggestions.add(key);
  trackEvent('HOME_INTEGRATION_SUGGESTION_SHOWN', {
    ...experiment,
    integration: suggestion.toolkit_slug,
    source: suggestion.source,
    action: suggestion.action,
    position,
    surface,
    on_screen: true,
  });
};

export const useSuggestionShown = (
  suggestion: IntegrationSuggestion,
  position: number,
  surface: SuggestionSurface
): RefCallback<HTMLDivElement> => {
  const { ref, inViewport } = useInViewport<HTMLDivElement>();
  const tabVisible = useTabVisible();

  useEffect(() => {
    if (!inViewport || !tabVisible) return;
    const shown = setTimeout(
      () => trackSuggestionShown(suggestion, position, surface),
      SHOWN_AFTER_MS
    );
    return () => clearTimeout(shown);
  }, [inViewport, position, suggestion, surface, tabVisible]);

  return ref;
};

export const suggestionTarget = (
  suggestion: IntegrationSuggestion
): IntegrationTarget | null => {
  if (suggestion.provider === 'on_device') return null;
  const reconnectId =
    suggestion.action === 'reconnect'
      ? (suggestion.account_id ?? undefined)
      : undefined;
  if (suggestion.provider === 'composio') {
    return {
      kind: 'integration',
      toolkitName: suggestion.integration_id,
      displayName: suggestion.display_name,
      requiresPaidPlan: suggestion.requires_paid_plan,
      reconnectAccountId: reconnectId,
    };
  }
  // A reconnect needs only the server id, so a custom server outside the catalog qualifies
  if (reconnectId) {
    return {
      kind: 'mcp',
      displayName: suggestion.display_name,
      requiresPaidPlan: suggestion.requires_paid_plan,
      reconnectServerId: reconnectId,
    };
  }
  if (!suggestion.mcp_url) return null;
  return {
    kind: 'mcp',
    catalogId: suggestion.toolkit_slug,
    displayName: suggestion.display_name,
    url: suggestion.mcp_url,
    requiresPaidPlan: suggestion.requires_paid_plan,
  };
};

export function useIntegrationSuggestions(surface: SuggestionSurface) {
  const enabled =
    hasFeature(FEATURES.HOME_INTEGRATION_SUGGESTIONS) ||
    hasFeature(FEATURES.INTEGRATION_SUGGESTIONS_CAMPAIGN);
  const queryClient = useQueryClient();
  const { data: suggestions, refetch } =
    useIntegrationSuggestionsQuery(enabled);
  const dismissMutation = useDismissIntegrationSuggestionMutation();
  const { data: servers } = useMcpServersQuery(enabled);
  const grants = useDevicePermissionGrants();
  const [removingAccountId, setRemovingAccountId] = useState<string | null>(
    null
  );

  // The web build refetches on focus through TanStack's own visibility
  // listener; Electron's window focus only reaches us as this IPC event.
  useEffect(() => {
    if (!enabled || !inElectron()) return;
    const controller = new AbortController();
    listenWithAbortIPC(
      'main-window-focused',
      () => {
        const state = queryClient.getQueryState(integrationKeys.suggestions());
        const age = Date.now() - (state?.dataUpdatedAt ?? 0);
        if (age >= INTEGRATION_SUGGESTIONS_REFRESH_INTERVAL_MS) void refetch();
      },
      controller.signal
    );
    return () => controller.abort();
  }, [enabled, queryClient, refetch]);

  const connect = async (suggestion: IntegrationSuggestion) => {
    if (suggestion.provider === 'on_device') {
      trackEvent('HOME_INTEGRATION_SUGGESTION_CONNECT_CLICKED', {
        ...suggestionExperimentProperties(suggestion, surface),
        integration: suggestion.toolkit_slug,
        source: suggestion.source,
        kind: 'on_device',
        surface,
      });
      await openOnDeviceSetup(
        suggestion.toolkit_slug,
        suggestion.display_name,
        surface === 'feed' ? 'feed' : 'home'
      );
      return;
    }
    const target = suggestionTarget(suggestion);
    if (!target) return;
    trackEvent('HOME_INTEGRATION_SUGGESTION_CONNECT_CLICKED', {
      ...suggestionExperimentProperties(suggestion, surface),
      integration: suggestion.toolkit_slug,
      source: suggestion.source,
      kind: target.kind,
      surface,
    });
    // A connected or reconnected toolkit leaves the server's list, or turns into a try entry, on the next read
    const refresh = () =>
      void queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    await connectIntegration(
      target,
      `${surface}_integration_suggestion:${suggestion.toolkit_slug}`,
      {
        onSettled: (success) => {
          if (success) {
            trackEvent('HOME_INTEGRATION_SUGGESTION_CONNECTED', {
              ...suggestionExperimentProperties(suggestion, surface),
              integration: suggestion.toolkit_slug,
              source: suggestion.source,
              kind: target.kind,
              surface,
            });
          }
          refresh();
        },
      }
    );
    refresh();
  };

  // A reconnect entry cannot be dismissed; removing the account is the way to make it go away
  const remove = async (suggestion: IntegrationSuggestion) => {
    const accountId = suggestion.account_id;
    if (!accountId) return;
    trackEvent('HOME_INTEGRATION_SUGGESTION_REMOVED', {
      integration: suggestion.toolkit_slug,
      kind: suggestion.provider,
      surface,
    });
    setRemovingAccountId(accountId);
    try {
      if (suggestion.provider === 'composio') {
        await backendApis.integrations.disconnectIntegration(accountId);
      } else {
        await backendApis.mcp.deleteServer(accountId);
      }
      queryClient.setQueryData<IntegrationSuggestion[]>(
        integrationKeys.suggestions(),
        (current) => current?.filter((s) => s.account_id !== accountId)
      );
      void queryClient.invalidateQueries({ queryKey: integrationKeys.all });
      void queryClient.invalidateQueries({ queryKey: mcpKeys.all });
    } catch {
      toast.error(`Failed to remove ${suggestion.display_name}`);
    } finally {
      setRemovingAccountId(null);
    }
  };

  const dismiss = (suggestion: IntegrationSuggestion) => {
    trackEvent('HOME_INTEGRATION_SUGGESTION_DISMISSED', {
      ...suggestionExperimentProperties(suggestion, surface),
      integration: suggestion.toolkit_slug,
      source: suggestion.source,
      action: suggestion.action,
      surface,
    });
    dismissMutation.mutate(suggestion.toolkit_slug);
  };

  // Trying it once is enough, so the entry goes away like a dismiss
  const tryIt = (suggestion: IntegrationSuggestion) => {
    trackEvent('HOME_INTEGRATION_SUGGESTION_TRY_CLICKED', {
      integration: suggestion.toolkit_slug,
      source: suggestion.source,
      surface,
    });
    startChatWithPrompt(suggestion.reason);
    dismissMutation.mutate(suggestion.toolkit_slug);
  };

  const installed = new Set<string>();
  for (const server of servers ?? []) {
    const id = templateIdForServer(server);
    if (id) installed.add(id);
  }
  for (const [id, granted] of Object.entries(grants)) {
    if (granted) installed.add(id);
  }
  const visible = visibleSuggestions(suggestions ?? [], {
    runs: (platforms) => inElectron() && supportedOnThisDevice(platforms),
    installed,
  });

  return {
    enabled,
    suggestions: visible,
    hasLoadedSuggestions: suggestions !== undefined,
    connect,
    dismiss,
    tryIt,
    remove,
    removingAccountId,
  };
}
